import type { TestOutcome } from "../bank/types.ts";
import type { SyntaxFailure } from "./execute.ts";

export type RunResult = {
  outcomes: TestOutcome[];
  logs: string[];
  /** Set when nothing ran: a compile error, a throw at the top level, or a timeout. */
  error?: {
    phase: "compile" | "execute" | "timeout";
    message: string;
    /** Present for a syntax error in the submitted code, so the UI can point at it. */
    syntax?: SyntaxFailure;
  };
};

/**
 * How long the tests get before the worker is killed. Generous enough that a
 * slow brute force still finishes, short enough that a `while (true)` costs you
 * seconds rather than the session.
 */
const TIMEOUT_MS = 5000;

/**
 * Compile and run one attempt in a throwaway worker.
 *
 * A fresh worker per run is the point, not an oversight: terminate() is the only
 * way to stop a runaway loop, and a terminated worker cannot be reused. It also
 * means no state leaks between runs, so a passing run is never the residue of a
 * previous one.
 *
 * `seed` is what the harness generates its inputs from. It is held constant for
 * the whole attempt, so pressing Run twice cannot hand you a different problem.
 */
export function runDrill(
  code: string,
  harness: string,
  prelude?: string,
  seed = 0,
): Promise<RunResult> {
  return new Promise((resolve) => {
    const worker = new Worker(new URL("./worker.ts", import.meta.url), { type: "module" });
    let settled = false;

    const finish = (result: RunResult) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      worker.terminate();
      resolve(result);
    };

    const timer = setTimeout(() => {
      finish({
        outcomes: [],
        logs: [],
        error: {
          phase: "timeout",
          message: `Timed out after ${TIMEOUT_MS / 1000}s. Usually an infinite loop, or recursion that never reaches its base case.`,
        },
      });
    }, TIMEOUT_MS);

    worker.onmessage = (event: MessageEvent) => {
      const data = event.data;
      if (data.ok) {
        finish({ outcomes: data.outcomes, logs: data.logs });
      } else {
        finish({
          outcomes: [],
          logs: data.logs,
          error: { phase: data.phase, message: data.message, syntax: data.syntax },
        });
      }
    };

    // Worker construction or module loading failed, which is a bug in the app
    // rather than in the submitted code. Say so, instead of blaming the user.
    worker.onerror = (event) => {
      finish({
        outcomes: [],
        logs: [],
        error: { phase: "execute", message: event.message || "The runner failed to start." },
      });
    };

    worker.postMessage({ code, harness, prelude, seed });
  });
}
